import api from "../utils/api";

// OpenAI Function Calling tool definitions
export const AI_TOOLS = [
  {
    type: "function" as const,
    function: {
      name: "get_vehicles",
      description: "Get a list of vehicles/units in the fleet with optional filters. Use this to answer questions about vehicles, units, or the fleet.",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number", description: "Number of vehicles to return (default: 20, max: 100)" },
          page: { type: "number", description: "Page number for pagination (default: 1)" },
          search: { type: "string", description: "Search by vehicle name" },
          status: { type: "string", description: "Filter by status" },
          type: { type: "string", description: "Filter by vehicle type" },
        },
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_vehicle_by_plate",
      description: "Get detailed information about a specific vehicle by its plate number",
      parameters: {
        type: "object",
        properties: {
          plate: { type: "string", description: "Vehicle plate number" },
        },
        required: ["plate"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_devices",
      description: "Get a list of GPS devices/trackers. Use this to check device status, connectivity, or find offline devices.",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number", description: "Number of devices to return (default: 20)" },
          page: { type: "number", description: "Page number (default: 1)" },
          status: { type: "string", description: "Filter by device status" },
          search: { type: "string", description: "Search by device model or identifier" },
        },
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_drivers",
      description: "Get a list of drivers in the fleet",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number", description: "Number of drivers to return (default: 20)" },
          page: { type: "number", description: "Page number (default: 1)" },
          search: { type: "string", description: "Search by driver name" },
        },
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_alerts",
      description: "Get recent alerts from the fleet (speed alerts, geofence breaches, maintenance alerts, etc.)",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number", description: "Number of alerts to return (default: 20)" },
          page: { type: "number", description: "Page number (default: 1)" },
          type: { type: "string", description: "Filter by alert type (e.g., speed, geofence, maintenance)" },
          status: { type: "string", description: "Filter by alert status" },
          vehicleId: { type: "string", description: "Filter by specific vehicle ID" },
        },
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_trips",
      description: "Get trip information and history",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number", description: "Number of trips to return (default: 20)" },
          page: { type: "number", description: "Page number (default: 1)" },
          status: { type: "string", description: "Filter by trip status (open, closed)" },
          vehicleId: { type: "string", description: "Filter by vehicle ID" },
          driverId: { type: "string", description: "Filter by driver ID" },
        },
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_geofences",
      description: "Get geofences (geographic zones) configured in the system",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number", description: "Number of geofences to return (default: 20)" },
          page: { type: "number", description: "Page number (default: 1)" },
          type: { type: "string", description: "Filter by geofence type" },
        },
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "get_fleet_summary",
      description: "Get a summary of the fleet including total counts of vehicles, devices, drivers, and recent activity",
      parameters: {
        type: "object",
        properties: {},
      },
    },
  },
];

// Tool execution functions
interface ToolResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

async function getVehicles(params: {
  limit?: number;
  page?: number;
  search?: string;
  status?: string;
  type?: string;
}): Promise<ToolResult> {
  try {
    const { data } = await api.get("/vehicleList", {
      params: {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        ...params,
      },
    });
    return {
      success: true,
      data: {
        vehicles: data.data || [],
        total: data.meta?.totalItems || data.data?.length || 0,
        page: params.page ?? 1,
        totalPages: data.meta?.totalPages || 1,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch vehicles" };
  }
}

async function getVehicleByPlate(params: { plate: string }): Promise<ToolResult> {
  try {
    const { data } = await api.get("/vehicle", {
      params: { plate: params.plate },
    });
    const vehicle = data?.data?.data ?? data?.data ?? data;
    return { success: true, data: vehicle };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch vehicle" };
  }
}

async function getDevices(params: {
  limit?: number;
  page?: number;
  status?: string;
  search?: string;
}): Promise<ToolResult> {
  try {
    const { data } = await api.get("/deviceList", {
      params: {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        ...params,
      },
    });
    return {
      success: true,
      data: {
        devices: data.data || [],
        total: data.meta?.totalItems || data.data?.length || 0,
        page: params.page ?? 1,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch devices" };
  }
}

async function getDrivers(params: {
  limit?: number;
  page?: number;
  search?: string;
}): Promise<ToolResult> {
  try {
    const { data } = await api.get("/driverList", {
      params: {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        ...params,
      },
    });
    return {
      success: true,
      data: {
        drivers: data.data || [],
        total: data.meta?.totalItems || data.data?.length || 0,
        page: params.page ?? 1,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch drivers" };
  }
}

async function getAlerts(params: {
  limit?: number;
  page?: number;
  type?: string;
  status?: string;
  vehicleId?: string;
}): Promise<ToolResult> {
  try {
    const { data } = await api.get("/alertsList", {
      params: {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        ...params,
      },
    });
    return {
      success: true,
      data: {
        alerts: data.data || [],
        total: data.meta?.totalItems || data.data?.length || 0,
        page: params.page ?? 1,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch alerts" };
  }
}

async function getTrips(params: {
  limit?: number;
  page?: number;
  status?: string;
  vehicleId?: string;
  driverId?: string;
}): Promise<ToolResult> {
  try {
    const { data } = await api.get("/tripsList", {
      params: {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        ...params,
      },
    });
    return {
      success: true,
      data: {
        trips: data.data || [],
        total: data.meta?.totalItems || data.data?.length || 0,
        page: params.page ?? 1,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch trips" };
  }
}

async function getGeofences(params: {
  limit?: number;
  page?: number;
  type?: string;
}): Promise<ToolResult> {
  try {
    const { data } = await api.get("/geofenceList", {
      params: {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        ...params,
      },
    });
    return {
      success: true,
      data: {
        geofences: data.data || [],
        total: data.meta?.totalItems || data.data?.length || 0,
        page: params.page ?? 1,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch geofences" };
  }
}

async function getFleetSummary(): Promise<ToolResult> {
  try {
    const [vehiclesRes, devicesRes, driversRes, alertsRes] = await Promise.allSettled([
      api.get("/vehicleList", { params: { limit: 1 } }),
      api.get("/deviceList", { params: { limit: 1 } }),
      api.get("/driverList", { params: { limit: 1 } }),
      api.get("/alertsList", { params: { limit: 5 } }),
    ]);

    const getTotal = (res: PromiseSettledResult<{ data: { meta?: { totalItems?: number }; data?: unknown[] } }>) => {
      if (res.status === "fulfilled") {
        return res.value.data.meta?.totalItems ?? res.value.data.data?.length ?? 0;
      }
      return 0;
    };

    const recentAlerts = alertsRes.status === "fulfilled" ? alertsRes.value.data.data || [] : [];

    return {
      success: true,
      data: {
        totalVehicles: getTotal(vehiclesRes),
        totalDevices: getTotal(devicesRes),
        totalDrivers: getTotal(driversRes),
        recentAlerts: recentAlerts,
        summary: `Fleet has ${getTotal(vehiclesRes)} vehicles, ${getTotal(devicesRes)} devices, and ${getTotal(driversRes)} drivers.`,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Failed to fetch fleet summary" };
  }
}

// Tool executor - maps tool names to functions
export async function executeTool(
  toolName: string,
  args: Record<string, unknown>
): Promise<ToolResult> {
  switch (toolName) {
    case "get_vehicles":
      return getVehicles(args as Parameters<typeof getVehicles>[0]);
    case "get_vehicle_by_plate":
      return getVehicleByPlate(args as Parameters<typeof getVehicleByPlate>[0]);
    case "get_devices":
      return getDevices(args as Parameters<typeof getDevices>[0]);
    case "get_drivers":
      return getDrivers(args as Parameters<typeof getDrivers>[0]);
    case "get_alerts":
      return getAlerts(args as Parameters<typeof getAlerts>[0]);
    case "get_trips":
      return getTrips(args as Parameters<typeof getTrips>[0]);
    case "get_geofences":
      return getGeofences(args as Parameters<typeof getGeofences>[0]);
    case "get_fleet_summary":
      return getFleetSummary();
    default:
      return { success: false, error: `Unknown tool: ${toolName}` };
  }
}
